/**
 * Item Search Service
 * Handles search logic and utilities for items
 */

const itemSearchService = (() => {
    // Search items based on search parameters
    const searchItems = (items, searchParams) => {
        if (!items || !Array.isArray(items)) {
            return [];
        }

        const { item_cd, item_name, staff_name, customer_name } = searchParams;

        return items.filter(item => {
            // Product code search
            const matchesCode = !item_cd || (item.product_code && item.product_code.toLowerCase().includes(item_cd.toLowerCase()));

            // Product name search
            const matchesName = !item_name || (item.name && item.name.toLowerCase().includes(item_name.toLowerCase()));

            // Staff name search
            const matchesStaff = !staff_name || (item.staff && item.staff.toLowerCase().includes(staff_name.toLowerCase()));

            // Customer name search
            const matchesCustomer = !customer_name || (item.customer && item.customer.toLowerCase().includes(customer_name.toLowerCase()));

            return matchesCode && matchesName && matchesStaff && matchesCustomer;
        });
    };

    // Advanced search with fuzzy matching
    const advancedSearch = (items, searchParams, options = {}) => {
        const { fuzzy = false, caseSensitive = false } = options;

        if (!fuzzy) {
            return searchItems(items, searchParams);
        }

        return items
            .map(item => {
                let relevanceScore = 0;
                const searchTerms = Object.values(searchParams).filter(term => term && term.trim());

                if (searchTerms.length === 0) {
                    return { item, relevanceScore: 1 };
                }

                // Calculate relevance score based on fuzzy matching
                searchTerms.forEach(term => {
                    const itemFields = [item.product_code, item.name, item.staff, item.customer].filter(field => field);

                    itemFields.forEach(field => {
                        const similarity = calculateSimilarity(term, field, caseSensitive);
                        relevanceScore += similarity;
                    });
                });

                return { item, relevanceScore: relevanceScore / searchTerms.length };
            })
            .filter(result => result.relevanceScore > 0.3)
            .sort((a, b) => b.relevanceScore - a.relevanceScore)
            .map(result => result.item);
    };

    // Calculate string similarity using Levenshtein distance
    const calculateSimilarity = (str1, str2, caseSensitive = false) => {
        if (!str1 || !str2) {
            return 0;
        }

        const s1 = caseSensitive ? str1 : str1.toLowerCase();
        const s2 = caseSensitive ? str2 : str2.toLowerCase();

        if (s1 === s2) {
            return 1;
        }

        const distance = levenshteinDistance(s1, s2);
        const maxLength = Math.max(s1.length, s2.length);

        return maxLength === 0 ? 0 : (maxLength - distance) / maxLength;
    };

    // Calculate Levenshtein distance between two strings
    const levenshteinDistance = (str1, str2) => {
        const matrix = [];

        for (let i = 0; i <= str2.length; i++) {
            matrix[i] = [i];
        }

        for (let j = 0; j <= str1.length; j++) {
            matrix[0][j] = j;
        }

        for (let i = 1; i <= str2.length; i++) {
            for (let j = 1; j <= str1.length; j++) {
                if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j] + 1);
                }
            }
        }

        return matrix[str2.length][str1.length];
    };

    // Search with autocomplete suggestions
    const getAutocompleteSuggestions = (items, field, query, limit = 10) => {
        if (!query || query.length < 2) {
            return [];
        }

        const suggestions = items
            .map(item => item[field])
            .filter(value => value && value.toLowerCase().includes(query.toLowerCase()))
            .filter((value, index, array) => array.indexOf(value) === index)
            .slice(0, limit);

        return suggestions;
    };

    // Search with filters applied
    const searchWithFilters = (items, searchParams, filters = {}) => {
        let filteredItems = searchItems(items, searchParams);

        // Apply additional filters
        if (filters.status) {
            filteredItems = filteredItems.filter(item => item.status && item.status.includes(filters.status));
        }

        if (filters.type) {
            filteredItems = filteredItems.filter(item => item.type === filters.type);
        }

        if (filters.stock) {
            switch (filters.stock) {
                case 'in-stock':
                    filteredItems = filteredItems.filter(item => item.stock > 0);
                    break;
                case 'out-of-stock':
                    filteredItems = filteredItems.filter(item => item.stock === 0);
                    break;
                case 'low-stock':
                    filteredItems = filteredItems.filter(item => item.stock > 0 && item.stock < 10);
                    break;
            }
        }

        return filteredItems;
    };

    return {
        searchItems,
        advancedSearch,
        getAutocompleteSuggestions,
        searchWithFilters
    };
})();

export default itemSearchService;
